import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Calendar } from "lucide-react";
import { useAuthorProfile, useAuthorPosts } from "@/hooks/useAuthorProfile";
import { AuthorCard } from "@/components/blog/AuthorCard";
import { SchemaMarkup } from "@/components/blog/SchemaMarkup";
import { generateAuthorProfileSchema } from "@/lib/authorSchemaGenerator";
import Footer from "@/components/Footer";
import { useMemo } from "react";

const AuthorProfile = () => {
  const { authorSlug } = useParams();
  const { data: author, isLoading: authorLoading } = useAuthorProfile(authorSlug || "");
  const { data: posts, isLoading: postsLoading } = useAuthorPosts(author?.id || "", "published");

  const schema = useMemo(() => {
    if (!author) return null;
    return generateAuthorProfileSchema(author, posts?.length || 0);
  }, [author, posts]);

  if (authorLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading author profile...</p>
      </div>
    );
  }

  if (!author) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Author Not Found</h1>
          <Link to="/blog">
            <Button>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Blog
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const authorName = author.full_name || "iQ Team";

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
      {schema && <SchemaMarkup schema={schema} />}
      
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 py-6">
          <Button variant="ghost" asChild>
            <Link to="/blog">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Blog
            </Link>
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Author Header */}
          <Card className="mb-8">
            <CardContent className="pt-6">
              <AuthorCard 
                author={author} 
                showBio={true} 
                showExpertise={true} 
                showSocial={true}
              />
              
              {author.credentials && (
                <div className="mt-4 pt-4 border-t">
                  <h3 className="text-sm font-semibold mb-2">Credentials & Qualifications</h3>
                  <p className="text-sm text-muted-foreground">{author.credentials}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Published Posts */}
          <div>
            <h2 className="text-2xl font-bold mb-6">
              Published Articles ({posts?.length || 0})
            </h2>

            {postsLoading ? (
              <p>Loading posts...</p>
            ) : posts && posts.length > 0 ? (
              <div className="grid gap-6">
                {posts.map((post) => (
                  <Card key={post.id} className="hover:shadow-lg transition-shadow">
                    <CardContent className="pt-6">
                      <Link to={`/blog/${post.slug}`}>
                        <h3 className="text-xl font-bold mb-2 hover:text-primary transition-colors">
                          {post.title}
                        </h3>
                      </Link>
                      {post.excerpt && (
                        <p className="text-muted-foreground mb-4">{post.excerpt}</p>
                      )}
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {new Date(post.published_at || post.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground">No published posts yet.</p>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AuthorProfile;
